import { GraphQLClient, gql } from 'graphql-request';

const DOMAIN = process.env.REACT_APP_SHOPIFY_DOMAIN;
const TOKEN = process.env.REACT_APP_SHOPIFY_STOREFRONT_TOKEN;
const VERSION = process.env.REACT_APP_SHOPIFY_API_VERSION || '2024-10';

// Shopify is fully disabled — products + checkout are managed natively.
// Keep the file so existing imports don't break; everything resolves to no-ops.
export const SHOPIFY_ENABLED = false;
const _RAW_SHOPIFY = Boolean(DOMAIN && TOKEN);

export const shopify = _RAW_SHOPIFY
  ? new GraphQLClient(`https://${DOMAIN}/api/${VERSION}/graphql.json`, {
      headers: {
        'X-Shopify-Storefront-Access-Token': TOKEN,
        'Content-Type': 'application/json',
      },
    })
  : null;

/* ============ Fragments ============ */
const PRODUCT_FRAGMENT = gql`
  fragment ProductCard on Product {
    id
    handle
    title
    description
    tags
    availableForSale
    options { id name values }
    priceRange {
      minVariantPrice { amount currencyCode }
    }
    compareAtPriceRange {
      minVariantPrice { amount currencyCode }
    }
    featuredImage { url altText }
    images(first: 8) {
      edges { node { url altText } }
    }
    variants(first: 30) {
      edges {
        node {
          id
          title
          availableForSale
          price { amount currencyCode }
          compareAtPrice { amount currencyCode }
          selectedOptions { name value }
        }
      }
    }
  }
`;

/* ============ Queries ============ */
export const GET_PRODUCTS = gql`
  ${PRODUCT_FRAGMENT}
  query Products($first: Int = 24, $query: String) {
    products(first: $first, query: $query) {
      edges { node { ...ProductCard } }
    }
  }
`;

export const GET_PRODUCT_BY_HANDLE = gql`
  ${PRODUCT_FRAGMENT}
  query ProductByHandle($handle: String!) {
    product(handle: $handle) { ...ProductCard }
  }
`;

/* ============ Cart Mutations / Queries ============ */
const CART_FRAGMENT = gql`
  fragment CartFields on Cart {
    id
    checkoutUrl
    totalQuantity
    cost {
      subtotalAmount { amount currencyCode }
      totalAmount { amount currencyCode }
    }
    lines(first: 50) {
      edges {
        node {
          id
          quantity
          merchandise {
            ... on ProductVariant {
              id
              title
              price { amount currencyCode }
              image { url altText }
              selectedOptions { name value }
              product { id title handle }
            }
          }
        }
      }
    }
  }
`;

export const CART_CREATE = gql`
  ${CART_FRAGMENT}
  mutation CartCreate($lines: [CartLineInput!]) {
    cartCreate(input: { lines: $lines }) {
      cart { ...CartFields }
      userErrors { field message }
    }
  }
`;

export const CART_QUERY = gql`
  ${CART_FRAGMENT}
  query CartQuery($id: ID!) {
    cart(id: $id) { ...CartFields }
  }
`;

export const CART_LINES_ADD = gql`
  ${CART_FRAGMENT}
  mutation CartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart { ...CartFields }
      userErrors { field message }
    }
  }
`;

export const CART_LINES_UPDATE = gql`
  ${CART_FRAGMENT}
  mutation CartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart { ...CartFields }
      userErrors { field message }
    }
  }
`;

export const CART_LINES_REMOVE = gql`
  ${CART_FRAGMENT}
  mutation CartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart { ...CartFields }
      userErrors { field message }
    }
  }
`;

/* ============ Helpers ============ */
export const formatMoney = (amount, currency = 'AUD') => {
  const n = typeof amount === 'string' ? parseFloat(amount) : amount;
  try {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `$${Math.round(n).toLocaleString()}`;
  }
};

/**
 * Normalise a Shopify product into the shape used by the existing UI.
 */
export const normalizeProduct = (p) => {
  if (!p) return null;
  const minVariant = p.variants?.edges?.[0]?.node;
  const price = parseFloat(p.priceRange?.minVariantPrice?.amount || minVariant?.price?.amount || 0);
  const compareAt = p.compareAtPriceRange?.minVariantPrice?.amount
    ? parseFloat(p.compareAtPriceRange.minVariantPrice.amount)
    : null;
  const currency = p.priceRange?.minVariantPrice?.currencyCode || 'AUD';
  const images = (p.images?.edges || []).map((e) => e.node.url);
  if (!images.length && p.featuredImage?.url) images.push(p.featuredImage.url);

  const variants = (p.variants?.edges || []).map((e) => e.node);
  // Try to find a size option
  const sizeOption = p.options?.find((o) => o.name.toLowerCase() === 'size');
  const sizes = sizeOption ? sizeOption.values : variants.map((v) => v.title);

  return {
    id: p.handle,
    shopifyId: p.id,
    name: p.title.replace(/\.$/, ''),
    handle: p.handle,
    description: p.description || '',
    price,
    compareAt: compareAt && compareAt > price ? compareAt : null,
    currency,
    images: images.length ? images : ['/placeholder.png'],
    sizes: sizes.length ? sizes : ['Default'],
    variants,
    availableForSale: p.availableForSale,
    tags: p.tags || [],
    tagline: p.tags?.[0] || null,
    details: (p.description || '')
      .split(/\n+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 6),
    collection: 'boucle-beds',
    badge: compareAt && compareAt > price ? 'Sale' : null,
  };
};

export const findVariantBySize = (product, size) => {
  if (!product?.variants?.length) return null;
  const match = product.variants.find((v) =>
    v.selectedOptions?.some((o) => o.name.toLowerCase() === 'size' && o.value === size)
  );
  return match || product.variants[0];
};
